/**
 * A conexão do WhatsApp do setor: QR, parear, testar, sincronizar.
 *
 * Um pedaço do objeto de ações do `Provedor` (`estado.jsx`), cortado por área.
 * Recebe o contexto uma vez, dentro do `useMemo` de lá: a identidade de cada
 * ação continua estável, como era no objeto único.
 */
import * as api from "../api.js";
import { confirmar, escolher } from "@/componentes/pergunta.tsx";

export function acoesDoWhatsapp(c) {
  const { avisar, mudar, tentar } = c;
  return {
    /**
     * Trocar de provedor e gravar credencial.
     *
     * Campo em branco não apaga o que já existe (é o servidor que decide isso):
     * a tela manda o formulário inteiro, e senha vazia significa "não mexi".
     */
    salvarConexaoWa: (provedor, credenciais) =>
      tentar("conexao", async () => {
        await api.definirConexaoWhatsapp(provedor, credenciais);
        mudar({ conexaoWa: await api.conexaoWhatsapp(), qrWa: null });
        avisar(`Conexão gravada: ${provedor}.`);
      }),

    /*
     * Conectar o WhatsApp Web — perguntando o que só dá para escolher agora.
     *
     * O celular entrega o histórico UMA vez, no pareamento: depois não existe
     * "agora eu quero". Baixar traz o contexto de quem já era cliente; não
     * baixar começa do zero, mais rápido e sem despejar no CRM a conversa
     * pessoal de um aparelho que também é de casa. Por isso a pergunta vem
     * ANTES do socket abrir, e a resposta fica guardada para as reconexões.
     */
    conectarWa: async () => {
      const escolha = await escolher({
        titulo: "Baixar as conversas antigas do celular?",
        texto:
          "O WhatsApp só entrega o histórico no momento do pareamento. Baixando, quem já era cliente aparece com a conversa inteira — leva alguns minutos e traz junto o que não é do escritório. Começando do zero, a caixa recebe daqui para frente.",
        acao: "Baixar histórico",
        alternativa: "Começar do zero",
      });
      /* Fechar a caixa é desistir de conectar — não é "começar do zero". */
      if (escolha === null) return;
      await tentar("conexao", async () => {
        const e = await api.conectarWhatsapp(escolha === "sim");
        const [conexaoWa, qr] = await Promise.all([api.conexaoWhatsapp(), api.qrWhatsapp().catch(() => null)]);
        mudar({ conexaoWa, qrWa: qr?.qr ?? null });
        avisar(e.recado ?? e.detalhe ?? "Conectando…");
      });
    },

    /*
     * Trocar o aparelho pareado — ou reparear o mesmo.
     *
     * "Conectar / ler QR" com pareamento válido NÃO mostra QR nenhum: ele
     * reconecta a conta que já está guardada, que é o certo no dia a dia. Para
     * ler um QR novo é preciso apagar o pareamento antes, e isso ninguém
     * adivinha — a pessoa clicava em conectar, nada aparecia, e a conclusão
     * era que o botão estava quebrado.
     *
     * Os dois passos ficam num clique só, com a confirmação dizendo o que se
     * perde (a sessão) e o que fica (conversa, contatos e arquivos).
     */
    novoQrWa: async () => {
      const ok = await confirmar({
        titulo: "Ler o QR de novo?",
        texto:
          "O pareamento atual é apagado e o CRM sai de 'Dispositivos conectados' no celular. Conversas, contatos e arquivos já guardados continuam aqui. Enquanto o QR não for lido, nada entra nem sai pelo WhatsApp.",
        acao: "Apagar e ler QR",
        perigo: true,
      });
      if (!ok) return;
      const escolha = await escolher({
        titulo: "Baixar as conversas antigas do celular?",
        texto:
          "É agora ou nunca: o WhatsApp só entrega o histórico no pareamento. Baixando, as conversas antigas entram na caixa; começando do zero, só o que chegar daqui para frente.",
        acao: "Baixar histórico",
        alternativa: "Começar do zero",
      });
      if (escolha === null) return;
      await tentar("conexao", async () => {
        await api.desconectarWhatsapp(true);
        const e = await api.conectarWhatsapp(escolha === "sim");
        const [conexaoWa, qr] = await Promise.all([api.conexaoWhatsapp(), api.qrWhatsapp().catch(() => null)]);
        mudar({ conexaoWa, qrWa: qr?.qr ?? null });
        avisar(qr?.qr ? "Leia o QR no celular: Aparelhos conectados → Conectar aparelho." : (e.detalhe ?? "Gerando o QR…"));
      });
    },

    /*
     * Desconectar é do escritório inteiro, não de quem clicou: enquanto
     * estiver fora, nenhuma mensagem entra nem sai — e o botão fica ao lado
     * de "Testar". Por isso pergunta, dizendo o que se perde em cada caso.
     */
    desconectarWa: async (desparear = false) => {
      const ok = await confirmar({
        titulo: desparear ? "Apagar o pareamento do WhatsApp?" : "Derrubar a conexão do WhatsApp?",
        texto: desparear
          ? "O CRM sai de 'Dispositivos conectados' no celular e só volta lendo o QR de novo. Conversas, contatos e arquivos já guardados ficam."
          : "Enquanto estiver desconectado, nada entra nem sai pelo WhatsApp do escritório. Reconectar é um clique, sem QR.",
        acao: desparear ? "Apagar o pareamento" : "Desconectar",
        perigo: true,
      });
      if (!ok) return;
      await tentar("conexao", async () => {
        await api.desconectarWhatsapp(desparear);
        mudar({ conexaoWa: await api.conexaoWhatsapp(), qrWa: null });
        avisar(desparear ? "Pareamento apagado — será preciso ler o QR de novo." : "Conexão derrubada.");
      });
    },

    testarWa: (telefone, texto) =>
      tentar("conexao", async () => {
        const r = await api.testarWhatsapp(telefone, texto);
        avisar(`Teste enviado pelo ${r.provedor}.`);
      }),

    sincronizarWa: () =>
      tentar("conexao", async () => {
        const r = await api.sincronizarWhatsapp();
        avisar(`Sincronizado: ${r.contatos ?? 0} contatos e ${r.novas ?? 0} mensagens novas.`);
      }),

    pedirHistoricoWa: () =>
      tentar("conexao", async () => {
        const r = await api.pedirHistorico();
        avisar(r?.detalhe ?? "Mais histórico pedido ao celular.");
      }),
  };
}
